<template>
    <pc-input-group>
        <pc-label
            v-bind:label="label"
            v-bind:show-required="required"
            v-bind:show-help="showHelp"
            v-bind:help-link="showHelpLinkOnly"
            v-on:help-clicked="emitHelpClicked" />
        <div>
            <slot name="help" />
        </div>
        <div
            v-if="hasCount"
            class="row gx-3">
            <pc-input
                v-bind:class="responsiveHelpers.short"
                v-bind:required="required"
                v-bind:readonly="readonly"
                v-bind:model-value="count"
                v-bind:name="`ExternalDataSelectorElement${element.PublicId} ${countFieldSuffix}`"
                v-bind:label="countFieldSuffix"
                v-bind:validator="countValidator"
                v-bind:show-validation-errors="false"
                inputmode="numeric"
                class="mb-3"
                is-sub-label
                v-on:update:model-value="sendDataChange(countId, $event)"
                v-on:validated="onCustomListValidated(countId, $event)" />
            <validation-render-helper v-bind:validation-error="validationErrors[countId]" />
        </div>
        <div
            v-for="(selector, i) in selectorFields"
            v-bind:key="selector.PublicId"
            class="row gx-3">
            <div
                v-bind:class="responsiveHelpers.standard"
                class="mb-3">
                <pc-loader
                    small
                    data-test-role="custom-list-loader"
                    v-bind:loading="isLoading || i >= isLoadingIndex">
                    <component
                        v-bind:is="selector.DisplayType == DISPLAY_TYPE.RadioButtons ? 'pc-radio-group' : 'pc-select'"
                        v-bind:name="`ExternalDataSelectorElement${element.PublicId} ${selector.Label}`"
                        v-bind:options="selectorOptions[selector.PublicId]"
                        v-bind:label="selector.Label"
                        v-bind:required="required"
                        v-bind:validator="validator"
                        v-bind:show-validation-errors="false"
                        v-bind:readonly="readonly"
                        v-bind:model-value="selectorValues[selector.PublicId]"
                        v-bind:disabled="
                            !selectorOptions[selector.PublicId] || selectorOptions[selector.PublicId].length == 0
                        "
                        is-sub-label
                        data-test-role="custom-list-input"
                        v-on:update:model-value="onSelectorInput(selector, i, $event)"
                        v-on:validated="onCustomListValidated(selector.PublicId, $event)" />
                </pc-loader>
            </div>
            <validation-render-helper v-bind:validation-error="validationErrors[selector.PublicId]" />
        </div>
        <div
            v-for="output in outputFields"
            v-bind:key="output.PublicId">
            <div v-if="output.DisplayType == DISPLAY_TYPE.Static">
                <div class="row gx-3">
                    <div
                        v-bind:class="responsiveHelpers.long"
                        class="mb-3 mb-0">
                        <pc-label
                            v-bind:label="output.Label"
                            v-bind:for-id="`ExternalDataSelectorElement${element.PublicId} ${output.Label}`"
                            muted
                            italic />
                    </div>
                </div>
                <div class="row gx-3">
                    <div class="mb-3 col-12">
                        <div
                            v-if="outputValues[output.PublicId] != ''"
                            v-bind:name="`ExternalDataSelectorElement${element.PublicId} ${output.Label}`"
                            data-test-role="custom-list-output">
                            {{ outputValues[output.PublicId] }}
                        </div>
                        <div
                            v-else
                            class="fst-italic">
                            {{ noValueMessage }}
                        </div>
                    </div>
                </div>
            </div>
            <div
                v-else-if="output.DisplayType == DISPLAY_TYPE.TextBox"
                class="row gx-3">
                <div
                    v-bind:class="responsiveHelpers.long"
                    class="mb-3">
                    <pc-input
                        v-bind:name="`ExternalDataSelectorElement${element.PublicId} ${output.Label}`"
                        v-bind:model-value="outputValues[output.PublicId]"
                        v-bind:disabled="true"
                        v-bind:label="output.Label"
                        is-sub-label
                        data-test-role="custom-list-output" />
                </div>
            </div>
        </div>
    </pc-input-group>
</template>
<script>
    import base, {
        SUFFIXES,
        mapGroupIdsAndValues,
        validationRenderHelper
    } from "Forms/Components/Elements/_elementBase";
    import { updateSelectorsStatic } from "./customList/customListUtil";
    import { THOUSAND_SEPARATOR_FORMATTER } from "Util/dataFormatters";
    import { AUX_MUTATION } from "Forms/Components/ProjectForm/projectFormStore";
    import { PROJECT_FORM_CONTROLLER_TYPES } from "../ProjectForm/projectFormControllerStore";
    import { deepClone } from "Util/util";
    import { mapI18n } from "Util/i18n";
    import { KEYS as customListKeys, NAMESPACE as customListNamespace } from "./i18n/customList.i18n";
    import { KEYS as sharedKeys, NAMESPACE as sharedNamespace } from "./i18n/sharedFormElementWords.i18n";
    import { getValidator, VALIDATOR_TYPES } from "Validation/validator";
    import { GlobalEventBus } from "Util/emitter";
    import { DISPLAY_TYPE } from "Forms/Components/Elements/customList/customListUtil";
    import { PROJECT_NAMESPACE } from "SharedStores/Project/projectStore";

    export default {
        components: {
            validationRenderHelper
        },
        extends: base,
        data() {
            return {
                isLoading: true,
                isLoadingIndex: Number.MAX_VALUE,
                selectorOptions: {},
                validationResults: {},
                DISPLAY_TYPE
            };
        },
        computed: {
            schema() {
                return this.element.AuxiliaryModel.ExternalDataSchema;
            },
            auxModel() {
                if (!this.projectElement) {
                    return null;
                }
                return this.projectElement.auxState || this.projectElement.AuxiliaryModel;
            },
            dataModel() {
                if (!this.auxModel) {
                    return {
                        Children: [],
                        IsObsolete: null,
                        LeafData: null,
                        RowId: null
                    };
                }
                return this.auxModel.CustomListData;
            },
            useDataSourceOrder() {
                return this.element.AuxiliaryModel.UseDataSourceOrder;
            },
            selectorFields() {
                return this.schema.SelectorFields;
            },
            outputFields() {
                return this.schema.OutputFields;
            },
            selectorValues() {
                const dict = {};
                this.selectorFields.forEach((sf) => {
                    dict[sf.PublicId] = this.getProjectValue(this.getFieldIdFromModel(sf));
                });
                return dict;
            },
            outputValues() {
                const dict = {};
                this.outputFields.forEach((of) => {
                    const rawValue = this.getProjectValue(this.getFieldIdFromModel(of));
                    const displayValue = of.ShowThousandSeparators
                        ? THOUSAND_SEPARATOR_FORMATTER.format(rawValue)
                        : rawValue;
                    dict[of.PublicId] = displayValue;
                });
                return dict;
            },
            hasCount() {
                return this.schema.CountField != null;
            },
            hasFilter() {
                return this.schema.FilterField != null;
            },
            ...mapGroupIdsAndValues({
                rowId: SUFFIXES.CustomList.RowId,
                count: SUFFIXES.CustomList.Count
            }),
            countFieldSuffix() {
                return SUFFIXES.CustomList.Count;
            },
            countValidator() {
                if (this.required) {
                    return getValidator(this.$app, [
                        VALIDATOR_TYPES.integer,
                        VALIDATOR_TYPES.required,
                        { type: VALIDATOR_TYPES.min, min: 0 }
                    ]);
                } else {
                    return getValidator(this.$app, [VALIDATOR_TYPES.integer, { type: VALIDATOR_TYPES.min, min: 0 }]);
                }
            },
            validationErrors() {
                const errors = {};
                for (const id in this.validationResults) {
                    if (this.validationResults.hasOwnProperty(id)) {
                        errors[id] = this.getValidationError(this.validationResults[id]);
                    }
                }
                return errors;
            },
            readonly() {
                return this.baseReadonly || this.isPrefill;
            },
            ...mapI18n(
                {
                    ...customListKeys
                },
                customListNamespace
            ),
            ...mapI18n(
                {
                    ...sharedKeys
                },
                sharedNamespace
            )
        },
        created() {
            if (!this.isPrefill) {
                this.updateCustomListData();
            } else {
                this.isLoading = false;
            }

            // Listen for specific form actions that can update the selector values for this custom list
            // outside of direct user interaction (connection call, data extractor)
            GlobalEventBus.on("update_custom_lists", this.updateCustomListsGlobalCallback);
        },
        unmounted() {
            GlobalEventBus.off("update_custom_lists", this.updateCustomListsGlobalCallback);
        },
        methods: {
            async updateCustomListData() {
                // Skip getting custom list data if we are not on the EditProject page
                if (!this.projectElement) {
                    this.updateSelectors({ isInitialLoad: true, forceSkipLazyLoading: true });
                    this.isLoading = false;
                    return;
                }

                // Get custom list data if we don't already have it stored in the aux state
                if (!this.auxModel.DataInitialized) {
                    const customListData = await this.fetchCustomListData();
                    this.commitCustomListData(customListData);
                }
                this.updateSelectors({ isInitialLoad: true });
                this.isLoading = false;
            },
            async fetchCustomListData() {
                return await (this.hasFilter ? this.fetchFullCustomListData() : this.fetchInitialCustomListData());
            },
            async fetchFullCustomListData() {
                const projectId = this.$store.state[PROJECT_NAMESPACE].ProjectId;
                const result = await this.$store.dispatch(
                    PROJECT_FORM_CONTROLLER_TYPES.actions.getCustomListDataFull,
                    { groupId: this.element.DataFieldGroupId, projectId },
                    { root: true }
                );
                return result.Data.CustomListData;
            },
            async fetchInitialCustomListData() {
                const selectors = [];
                for (let i = 0; i < this.selectorFields.length; i++) {
                    const sf = this.selectorFields[i];
                    if (!this.selectorValues[sf.PublicId]) {
                        break;
                    }
                    selectors.push(this.selectorValues[sf.PublicId]);
                }
                const result = await this.$store.dispatch(
                    PROJECT_FORM_CONTROLLER_TYPES.actions.getCustomListDataInitial,
                    { groupId: this.element.DataFieldGroupId, selectors: selectors },
                    { root: true }
                );
                return result.Data.CustomListData;
            },
            async lazilyLoadSelectionChildren(selectorFields, selectorValues, index) {
                this.isLoadingIndex = index;
                const selectors = [];
                for (let i = 0; i < index; i++) {
                    selectors.push(selectorValues[selectorFields[i].PublicId]);
                }

                let lookupResult;
                try {
                    lookupResult = await this.$store.dispatch(
                        PROJECT_FORM_CONTROLLER_TYPES.actions.getCustomListDataChildren,
                        { groupId: this.element.DataFieldGroupId, selectors: selectors },
                        { root: true }
                    );
                } finally {
                    this.isLoadingIndex = Number.MAX_VALUE;
                }

                if (lookupResult.status === "success" && lookupResult.Data?.CustomListSubroot) {
                    return lookupResult.Data.CustomListSubroot;
                } else {
                    throw new Error(`Could not load children for custom list with selector values: ${selectors}`);
                }
            },
            commitCustomListData(customListData) {
                const auxState = {
                    CustomListData: customListData,
                    DataInitialized: true
                };
                this.$store.commit(AUX_MUTATION, {
                    elementId: this.element.PublicId,
                    auxState: auxState
                });
            },
            getSelectorId(baseId) {
                return `Selector-${baseId}`;
            },
            getFieldIdFromModel(modelField) {
                return this.element.GroupDataFields ? this.element.GroupDataFields[modelField.Label] : null;
            },
            async updateSelectors({ isInitialLoad, forceSkipLazyLoading }) {
                if (!this.element.GroupDataFields) {
                    return; // The custom list is empty, nothing to do here
                }
                // A clone of the custom list data is used so data can be lazily loaded without
                // modifying the vuex store. If the update function makes changes to the custom list,
                // those changes are committed below.
                const customListModel = deepClone(this.dataModel);

                // When a filter is present, we load the full, filtered Custom List data. If the
                //  filter yields no matching node children, updateSelectorStatic assumes the node
                //  is not fully loaded and attempts a lazy load, effectively fetching all possible
                //  selections. So, skip lazy loading in this case.
                const skipLazyLoading = forceSkipLazyLoading || this.hasFilter;
                const updateResult = await updateSelectorsStatic(
                    customListModel,
                    this.selectorFields,
                    this.selectorValues,
                    this.selectDefaultValue,
                    this.rowId,
                    isInitialLoad,
                    skipLazyLoading ? null : this.lazilyLoadSelectionChildren
                );

                this.selectorOptions = updateResult.selectorOptions;

                updateResult.fieldsToClear.forEach((sf) => {
                    this.sendDataChange(this.getFieldIdFromModel(sf), "");
                });

                if (updateResult.selectedObsoleteNodeToPopulate) {
                    this.fillSelectedObsoleteLeafNode(updateResult.selectedObsoleteNodeToPopulate);
                }

                if (updateResult.mutatedCustomListData) {
                    this.commitCustomListData(customListModel);
                }

                // If it's the initial load, don't change or clear any
                // existing data. This guarantees the correct behavior in 2 edge cases:
                // 1. The project was saved with a row that is now completely obsolete.
                //    In this case we don't want to empty the saved values on load. If the
                //    user selects a different value and saves, the obsolete value will no
                //    longer be available.
                // 2. An output value was modified for the saved set of selector values. If the
                //    project was saved with the now-obsolete output value, we don't want
                //    that to change just because the user opened the form again. If the user
                //    deselects and reselects the same values, they will load the updated output
                //    values for that row.
                if (isInitialLoad) {
                    return;
                }

                // Full-selection made: populate the output values
                if (updateResult.rowId) {
                    this.fillOutputValues(updateResult.rowId, updateResult.outputValues);
                }
                // Incomplete selection: clear output values
                else {
                    this.emptyOutputValues();
                }
            },
            // If a row has been completely removed from the data source, but is
            // saved in the project, fabricate a node in the group data to represent
            // the obsolete selection. This means filling in the leafData and rowId
            // as if it were any other leaf node. This allows the user to deselect and
            // reselect the obsolete row as long as the page hasn't been refreshed.
            fillSelectedObsoleteLeafNode(leafNode) {
                // LeafData is a simple list of strings in the order of the output fields
                const leafData = [];
                this.outputFields.forEach((outputField) => {
                    const outputValue = this.getProjectValue(this.getFieldIdFromModel(outputField));
                    leafData.push(outputValue);
                });
                leafNode.LeafData = leafData;
                leafNode.RowId = this.rowId;
            },
            onSelectorInput(selectorField, index, value) {
                this.sendDataChange(this.getFieldIdFromModel(selectorField), value);

                // Clear any saved values for subsequent selectors
                // or updating selectors gets screwed up by stale
                // values
                index++;
                for (; index < this.selectorFields.length; index++) {
                    const sf = this.selectorFields[index];
                    this.sendDataChange(this.getFieldIdFromModel(sf), "");
                }

                this.updateSelectors({ isInitialLoad: false });
            },
            fillOutputValues(rowId, leafData) {
                for (let i = 0; i < this.outputFields.length; i++) {
                    const of = this.outputFields[i];
                    const val = leafData[i];
                    this.sendDataChange(this.getFieldIdFromModel(of), val);
                }
                this.sendDataChange(this.rowIdId, rowId);
            },
            emptyOutputValues() {
                for (let i = 0; i < this.outputFields.length; i++) {
                    const of = this.outputFields[i];
                    this.sendDataChange(this.getFieldIdFromModel(of), "");
                }
                this.sendDataChange(this.rowIdId, "");
            },
            onCustomListValidated(fieldId, result) {
                this.validationResults[fieldId] = result;
            },
            getValidationError(validationResult) {
                if (validationResult.error) {
                    return validationResult.error;
                }
                return null;
            },
            updateCustomListsGlobalCallback() {
                this.$store.commit(AUX_MUTATION, {
                    elementId: this.element.PublicId,
                    auxState: { DataInitialized: false }
                });
                this.updateCustomListData();
            }
        }
    };
</script>
